// Bilinguale Service-Detail-Inhalte (DE / EN)
import { useLanguage } from "../context/LanguageContext";
import heroImg from "./pictures/hero.jpg";
import aboutImg from "./pictures/about.jpg";
import carImg from "./pictures/car.jpg";
import aiImg from "./pictures/ai.jpg";

// ---------------------- DEUTSCH ----------------------
const de = {
  "it-architektur": {
    slug: "it-architektur",
    icon: "LayoutGrid",
    eyebrow: "Leistung 01",
    title: "IT-Architektur & Systemintegration",
    subtitle:
      "Skalierbare, zukunftsfähige IT-Landschaften – von Fahrzeug-Backend-Systemen über Cloud-native Plattformen bis hin zu sicheren digitalen Services.",
    image: heroImg,
    intro:
      "Komplexe Systemlandschaften brauchen klare Strukturen. Ich entwickle IT-Architekturen, die sowohl die heutigen Anforderungen abbilden, als auch ausreichend Spielraum für künftige Geschäftsmodelle, Märkte und Technologien lassen – methodisch, dokumentiert und nachvollziehbar.",
    pillars: [
      { icon: "Cloud", title: "Cloud- & Hybrid-Architektur", desc: "Architekturentwürfe für Public-, Private- und Hybrid-Cloud-Setups mit Fokus auf Skalierung, Kosteneffizienz und Compliance." },
      { icon: "Network", title: "API & Service-Design", desc: "API-First-Ansatz, klare Service-Schnitte, Event-driven Architekturen und Domain-Driven Design für nachhaltige Plattformen." },
      { icon: "ShieldCheck", title: "Security by Design", desc: "Sicherheits- und Datenschutzanforderungen werden vom ersten Strich des Architekturbildes mitgedacht – nicht erst am Ende." },
      { icon: "Boxes", title: "Systemintegration", desc: "Integration von Backend-, Fahrzeug- und Drittsystemen über stabile, performante und observierbare Schnittstellen." },
    ],
    approach: [
      { step: "01", title: "Discovery", desc: "Analyse von Geschäftszielen, Stakeholder-Anforderungen und bestehender Systemlandschaft." },
      { step: "02", title: "Architektur-Zielbild", desc: "Erarbeitung eines klaren Zielbilds inkl. Bausteine, Schnittstellen und Roadmap." },
      { step: "03", title: "Bewertung & Auswahl", desc: "Technologie- und Vendor-Bewertung anhand objektiver Kriterien (TCO, Risiken, Reifegrad)." },
      { step: "04", title: "Umsetzung & Steuerung", desc: "Begleitung der Implementierung als Architekt, Sparringspartner oder Reviewer." },
      { step: "05", title: "Operate & Evolve", desc: "Aufbau von Architektur-Governance, Reviews und Weiterentwicklung im Betrieb." },
    ],
    deliverables: [
      "Architektur-Zielbild (C4-Modell, Bausteine, Schnittstellen)",
      "Technologie- & Vendor-Bewertungen",
      "Integrationskonzepte und API-Spezifikationen",
      "Security- & Datenschutz-Konzepte (Privacy by Design)",
      "Migrations- und Modernisierungsroadmaps",
      "Architektur-Reviews und Quality Gates",
    ],
    tech: ["AWS · Azure · GCP", "Kubernetes · Container-Plattformen", "REST · GraphQL · gRPC", "Kafka · Event-driven", "OAuth2 · OIDC · mTLS", "C4 · ArchiMate · TOGAF-Elemente"],
    useCases: [
      { title: "Connected-Car Backend", desc: "Aufbau einer skalierbaren Backend-Plattform für Telematik-Daten und Connected Services über mehrere Märkte hinweg." },
      { title: "eSIM-Plattform", desc: "Architektur für eine Embedded-SIM-Lösung inkl. Anbindung von MNOs, Self-Service-Portalen und Fleet-Management." },
      { title: "Legacy-Modernisierung", desc: "Modularisierung gewachsener Monolithen in Domain-orientierte Services – schrittweise und risikoarm." },
    ],
  },
  "projekt-testmanagement": {
    slug: "projekt-testmanagement",
    icon: "Workflow",
    eyebrow: "Leistung 02",
    title: "Projekt- & Testmanagement",
    subtitle: "Steuerung internationaler IT-Rollouts und konsequente Qualitätssicherung – damit komplexe Produkte sicher in den Markt gelangen.",
    image: carImg,
    intro:
      "Projekte im Connected-Car- und Telco-Umfeld scheitern selten an der Technik, sondern an Schnittstellen, unklaren Anforderungen und fehlender Steuerung. Ich übernehme die Verantwortung für Lieferung, Qualität und marktspezifische Einführung – pragmatisch und mit Blick für Risiken.",
    pillars: [
      { icon: "Target", title: "Programm- & Projektleitung", desc: "Erfahrene Steuerung großer, mehrstufiger Vorhaben mit verteilten Teams und mehreren Stakeholdern." },
      { icon: "ClipboardCheck", title: "Testmanagement & QA", desc: "Aufbau und Steuerung von Testprozessen – von Teststrategie über Automatisierung bis zur Abnahme." },
      { icon: "Globe", title: "Internationale Rollouts", desc: "Markt- und länderspezifische Einführungen unter Berücksichtigung regulatorischer Anforderungen." },
      { icon: "Activity", title: "Risiko- & Eskalations-Management", desc: "Frühzeitiges Erkennen kritischer Themen, klare Kommunikation und konsequente Eskalation, wenn nötig." },
    ],
    approach: [
      { step: "01", title: "Projekt-Setup", desc: "Klare Ziele, Scope, Governance, Rollen und Kommunikationspfade definieren." },
      { step: "02", title: "Planung & Backlog", desc: "Realistische Planung (klassisch, agil oder hybrid) mit transparentem Backlog und Meilensteinen." },
      { step: "03", title: "Test-Strategie", desc: "Risikobasierte Teststrategie, Testautomatisierung und definierte Qualitäts-Gates." },
      { step: "04", title: "Rollout & Steuerung", desc: "Stufenweise Einführung, Monitoring von KPIs und kontinuierliche Steuerung." },
      { step: "05", title: "Hypercare & Übergabe", desc: "Stabilisierung nach Go-Live, Wissenstransfer und Übergabe in den Regelbetrieb." },
    ],
    deliverables: [
      "Projekt- und Programmpläne",
      "Risiko- und Stakeholder-Register",
      "Test-Strategien und Test-Pläne",
      "Statusreports und Steering-Vorlagen",
      "Go/No-Go-Empfehlungen für Releases",
      "Hypercare- und Übergabekonzepte",
    ],
    tech: ["Scrum · Kanban · SAFe", "Jira · Confluence · Azure DevOps", "Test-Automation (Selenium, Cypress, Playwright)", "API- & Last-Tests (Postman, k6, JMeter)", "CI/CD-Integration", "Quality Gates & DoD"],
    useCases: [
      { title: "Markt-Rollout Connected Services", desc: "Einführung neuer Connected-Car-Services in 10+ Ländern mit unterschiedlichen regulatorischen Anforderungen." },
      { title: "OTA-Update-Programm", desc: "Steuerung eines OTA-Programms inkl. Test-Setup, Pilot-Flotten und kontrolliertem Massen-Rollout." },
      { title: "Telco-Backend-Migration", desc: "Koordinierter Wechsel zentraler Backend-Komponenten ohne Beeinträchtigung des Endkunden-Service." },
    ],
  },
  "business-analyse": {
    slug: "business-analyse",
    icon: "LineChart",
    eyebrow: "Leistung 03",
    title: "Business-Analyse & Prozessoptimierung",
    subtitle: "Geschäftsprozesse verstehen, gezielt neu denken und IT-gestützt umsetzen – fundiert mit Lean Six Sigma, Scrum und SAFe.",
    image: aboutImg,
    intro:
      "Gute IT-Lösungen entstehen aus klar verstandenen Geschäftsprozessen. Ich analysiere bestehende Abläufe, decke Brüche und Verschwendung auf und entwerfe gemeinsam mit Fach- und IT-Bereich neue, durchgängige Prozesse – mit messbarem Effekt auf Qualität, Durchlaufzeit und Kosten.",
    pillars: [
      { icon: "Search", title: "Prozess-Analyse", desc: "Strukturierte Aufnahme bestehender Prozesse, Identifikation von Engpässen, Medienbrüchen und Risiken." },
      { icon: "Layers", title: "Anforderungs-Management", desc: "Saubere Anforderungen als Brücke zwischen Fachbereich und Entwicklung – inkl. User Stories, Akzeptanzkriterien und Modellen." },
      { icon: "TrendingUp", title: "Prozess-Reengineering", desc: "Neugestaltung end-to-end – datengetrieben, mit klarem Zielbild und realistischem Umsetzungsplan." },
      { icon: "BarChart3", title: "KPI & Wirkungs-Messung", desc: "Definition aussagekräftiger Kennzahlen, um Wirkung und Erfolg von Veränderungen messbar zu machen." },
    ],
    approach: [
      { step: "01", title: "Ist-Aufnahme", desc: "Interviews, Workshops und Datenanalyse zur Erfassung des aktuellen Prozesses." },
      { step: "02", title: "Analyse & Bewertung", desc: "Identifikation von Schwachstellen, Verschwendung und Automatisierungspotenzialen." },
      { step: "03", title: "Soll-Konzept", desc: "Entwurf eines Zielprozesses inkl. Rollen, Systemen und Datenflüssen." },
      { step: "04", title: "Umsetzungsplan", desc: "Priorisierung von Maßnahmen, Aufwand-Nutzen-Bewertung, Roadmap." },
      { step: "05", title: "Begleitung & Review", desc: "Begleitung der Umsetzung und regelmäßige Wirksamkeitsprüfung anhand der KPIs." },
    ],
    deliverables: [
      "Prozessmodelle (BPMN, Value Stream Maps)",
      "Anforderungsdokumente und User Stories",
      "Soll-Prozess-Designs und Datenflussmodelle",
      "Business Cases und Aufwandsschätzungen",
      "KPI-Frameworks und Reporting-Templates",
      "Change- und Schulungskonzepte",
    ],
    tech: ["Lean Six Sigma", "Scrum · SAFe · Kanban", "BPMN 2.0 · Value Stream Mapping", "Design Thinking · Service Blueprinting", "Anforderungs-Tools (Jira, Confluence)", "Daten- und Prozess-Mining"],
    useCases: [
      { title: "Customer Journey Connected Services", desc: "Durchgängige Optimierung der digitalen Customer Journey vom Kauf bis zum Vertragsende." },
      { title: "Order-to-Delivery in der Telco", desc: "Reengineering eines fragmentierten Order-Prozesses mit deutlicher Reduktion der Durchlaufzeit." },
      { title: "Operations-Excellence im Backend", desc: "Aufbau von Incident-, Problem- und Change-Prozessen für eine kritische Plattform." },
    ],
  },
  "ki-beratung": {
    slug: "ki-beratung",
    icon: "Sparkles",
    eyebrow: "Leistung 04",
    title: "KI-Beratung & Datenstrategie",
    subtitle: "Künstliche Intelligenz wirtschaftlich, sicher und verantwortungsvoll in bestehende Wertschöpfungsketten integrieren – vom Use-Case bis zum produktiven Betrieb.",
    image: aiImg,
    intro:
      "KI ist heute kein technisches Experiment mehr, sondern ein strategischer Hebel. Entscheidend ist, sie dort einzusetzen, wo sie echten Mehrwert schafft – mit klarer Wirtschaftlichkeit, sauberer Datenbasis und einem Betriebsmodell, das auch Sicherheits-, Compliance- und ethische Anforderungen erfüllt.",
    pillars: [
      { icon: "Lightbulb", title: "KI-Use-Case-Identifikation", desc: "Strukturierte Identifikation und Priorisierung von KI-Use-Cases entlang der Wertschöpfungskette." },
      { icon: "Database", title: "Daten- & MLOps-Strategie", desc: "Aufbau der Datenbasis, Plattform und Prozesse, die produktive KI-Lösungen tragen." },
      { icon: "ShieldCheck", title: "Verantwortungsvolle KI", desc: "EU-AI-Act, Datenschutz, Modell-Governance und Bias-Management – sauber von Anfang an gedacht." },
      { icon: "Rocket", title: "Skalierung & Operate", desc: "Vom Pilot in die Produktion: Architektur, Betrieb und kontinuierliche Verbesserung von KI-Lösungen." },
    ],
    approach: [
      { step: "01", title: "KI-Reifegrad-Check", desc: "Standortbestimmung zu Daten, Technologie, Organisation und Use-Case-Pipeline." },
      { step: "02", title: "Use-Case-Mapping", desc: "Identifikation und Bewertung relevanter Use-Cases nach Wirkung, Aufwand und Risiko." },
      { step: "03", title: "Pilot & PoC", desc: "Schneller, fokussierter Pilot mit klarem Erfolgsmaßstab – statt langer Konzeptphasen." },
      { step: "04", title: "Produktivierung", desc: "Überführung in eine produktive, beobachtbare und governance-konforme Lösung." },
      { step: "05", title: "Skalierung & Operate", desc: "Roll-out auf weitere Use-Cases, kontinuierliches Monitoring, Modell-Lifecycle." },
    ],
    deliverables: [
      "KI-Strategie- und Roadmap-Dokumente",
      "Use-Case-Portfolio mit Priorisierung und Business Cases",
      "Daten- und MLOps-Zielbild",
      "PoC- und Pilot-Konzepte inkl. Erfolgskriterien",
      "Governance-, Risiko- und EU-AI-Act-Konzepte",
      "Architektur-Vorlagen für GenAI- und ML-Lösungen",
    ],
    tech: ["Generative KI · LLMs · RAG", "Klassisches ML · Time-Series · Vision", "MLOps · Modell-Monitoring", "Vector-DBs · Knowledge Graphs", "Data Mesh · Lakehouse", "EU AI Act · DSGVO · ISO 42001"],
    useCases: [
      { title: "KI-Assistent für Fahrzeugdaten", desc: "Natürlichsprachiger Zugriff auf komplexe Telematik- und Diagnose-Daten für Service-Mitarbeiter." },
      { title: "Predictive Quality im Rollout", desc: "Vorhersage kritischer Qualitätsprobleme bei internationalen Rollouts auf Basis historischer Tests." },
      { title: "GenAI im Customer Service", desc: "Co-Pilot-Lösungen für Service-Center mit sauberer Wissensbasis, Guardrails und Audit-Trail." },
    ],
  },
};

// ---------------------- ENGLISH ----------------------
const en = {
  "it-architektur": {
    slug: "it-architektur",
    icon: "LayoutGrid",
    eyebrow: "Service 01",
    title: "IT Architecture & System Integration",
    subtitle:
      "Scalable, future-ready IT landscapes – from vehicle backend systems through cloud-native platforms to secure digital services.",
    image: heroImg,
    intro:
      "Complex system landscapes need clear structures. I design IT architectures that reflect today's requirements while leaving enough room for future business models, markets and technologies – methodically, documented and traceable.",
    pillars: [
      { icon: "Cloud", title: "Cloud & hybrid architecture", desc: "Architecture designs for public, private and hybrid cloud setups with a focus on scalability, cost efficiency and compliance." },
      { icon: "Network", title: "API & service design", desc: "API-first approach, clear service boundaries, event-driven architectures and domain-driven design for sustainable platforms." },
      { icon: "ShieldCheck", title: "Security by design", desc: "Security and data protection requirements are baked into the architecture from the first stroke – not bolted on at the end." },
      { icon: "Boxes", title: "System integration", desc: "Integration of backend, vehicle and third-party systems via stable, performant and observable interfaces." },
    ],
    approach: [
      { step: "01", title: "Discovery", desc: "Analysis of business goals, stakeholder requirements and the existing system landscape." },
      { step: "02", title: "Target architecture", desc: "Definition of a clear target picture including building blocks, interfaces and roadmap." },
      { step: "03", title: "Assessment & selection", desc: "Technology and vendor assessment against objective criteria (TCO, risk, maturity)." },
      { step: "04", title: "Delivery & guidance", desc: "Accompanying implementation as architect, sparring partner or reviewer." },
      { step: "05", title: "Operate & evolve", desc: "Establishing architecture governance, reviews and ongoing evolution in operations." },
    ],
    deliverables: [
      "Target architecture (C4 model, building blocks, interfaces)",
      "Technology & vendor assessments",
      "Integration concepts and API specifications",
      "Security & privacy concepts (privacy by design)",
      "Migration and modernization roadmaps",
      "Architecture reviews and quality gates",
    ],
    tech: ["AWS · Azure · GCP", "Kubernetes · Container platforms", "REST · GraphQL · gRPC", "Kafka · Event-driven", "OAuth2 · OIDC · mTLS", "C4 · ArchiMate · TOGAF elements"],
    useCases: [
      { title: "Connected-car backend", desc: "Building a scalable backend platform for telematics data and connected services across multiple markets." },
      { title: "eSIM platform", desc: "Architecture for an embedded-SIM solution including connectivity to MNOs, self-service portals and fleet management." },
      { title: "Legacy modernization", desc: "Modularizing legacy monoliths into domain-oriented services – step by step and with low risk." },
    ],
  },
  "projekt-testmanagement": {
    slug: "projekt-testmanagement",
    icon: "Workflow",
    eyebrow: "Service 02",
    title: "Project & Test Management",
    subtitle: "Steering international IT rollouts and consistent quality assurance – so complex products reach the market safely.",
    image: carImg,
    intro:
      "Projects in the connected-car and telco space rarely fail because of technology, but because of interfaces, unclear requirements and missing steering. I take responsibility for delivery, quality and market-specific introduction – pragmatic and with an eye for risks.",
    pillars: [
      { icon: "Target", title: "Programme & project leadership", desc: "Experienced steering of large, multi-phase initiatives with distributed teams and many stakeholders." },
      { icon: "ClipboardCheck", title: "Test management & QA", desc: "Building and running test processes – from test strategy and automation through to formal acceptance." },
      { icon: "Globe", title: "International rollouts", desc: "Market- and country-specific introductions while respecting regulatory requirements." },
      { icon: "Activity", title: "Risk & escalation management", desc: "Spotting critical topics early, communicating clearly and escalating decisively when needed." },
    ],
    approach: [
      { step: "01", title: "Project setup", desc: "Defining clear goals, scope, governance, roles and communication paths." },
      { step: "02", title: "Planning & backlog", desc: "Realistic planning (classic, agile or hybrid) with a transparent backlog and milestones." },
      { step: "03", title: "Test strategy", desc: "Risk-based test strategy, test automation and clearly defined quality gates." },
      { step: "04", title: "Rollout & steering", desc: "Staged introduction, KPI monitoring and continuous steering." },
      { step: "05", title: "Hypercare & handover", desc: "Post go-live stabilization, knowledge transfer and handover to operations." },
    ],
    deliverables: [
      "Project and programme plans",
      "Risk and stakeholder register",
      "Test strategies and test plans",
      "Status reports and steering decks",
      "Go/No-Go recommendations for releases",
      "Hypercare and handover concepts",
    ],
    tech: ["Scrum · Kanban · SAFe", "Jira · Confluence · Azure DevOps", "Test automation (Selenium, Cypress, Playwright)", "API & load tests (Postman, k6, JMeter)", "CI/CD integration", "Quality gates & DoD"],
    useCases: [
      { title: "Connected services market rollout", desc: "Introducing new connected-car services in 10+ countries with different regulatory requirements." },
      { title: "OTA update programme", desc: "Steering an OTA programme including test setup, pilot fleets and controlled mass rollout." },
      { title: "Telco backend migration", desc: "Coordinated replacement of central backend components without impacting the end-customer service." },
    ],
  },
  "business-analyse": {
    slug: "business-analyse",
    icon: "LineChart",
    eyebrow: "Service 03",
    title: "Business Analysis & Process Optimization",
    subtitle: "Understand business processes, rethink them deliberately and implement them IT-supported – grounded in Lean Six Sigma, Scrum and SAFe.",
    image: aboutImg,
    intro:
      "Good IT solutions grow from clearly understood business processes. I analyse existing workflows, uncover breaks and waste, and design new end-to-end processes together with business and IT – with a measurable effect on quality, lead time and cost.",
    pillars: [
      { icon: "Search", title: "Process analysis", desc: "Structured capture of existing processes; identification of bottlenecks, media breaks and risks." },
      { icon: "Layers", title: "Requirements management", desc: "Clean requirements as a bridge between business and engineering – including user stories, acceptance criteria and models." },
      { icon: "TrendingUp", title: "Process re-engineering", desc: "End-to-end redesign – data-driven, with a clear target picture and a realistic implementation plan." },
      { icon: "BarChart3", title: "KPI & impact measurement", desc: "Defining meaningful KPIs to make the impact and success of changes measurable." },
    ],
    approach: [
      { step: "01", title: "As-is capture", desc: "Interviews, workshops and data analysis to capture the current process." },
      { step: "02", title: "Analysis & assessment", desc: "Identification of weaknesses, waste and automation potential." },
      { step: "03", title: "To-be concept", desc: "Designing a target process including roles, systems and data flows." },
      { step: "04", title: "Implementation plan", desc: "Prioritization of measures, cost-benefit assessment, roadmap." },
      { step: "05", title: "Guidance & review", desc: "Accompanying implementation and regular effectiveness checks against the KPIs." },
    ],
    deliverables: [
      "Process models (BPMN, value stream maps)",
      "Requirements documents and user stories",
      "Target-process designs and data flow models",
      "Business cases and effort estimates",
      "KPI frameworks and reporting templates",
      "Change and training concepts",
    ],
    tech: ["Lean Six Sigma", "Scrum · SAFe · Kanban", "BPMN 2.0 · Value stream mapping", "Design Thinking · Service blueprinting", "Requirements tools (Jira, Confluence)", "Data & process mining"],
    useCases: [
      { title: "Customer journey – connected services", desc: "End-to-end optimization of the digital customer journey from purchase to contract end." },
      { title: "Order-to-delivery in telco", desc: "Re-engineering of a fragmented order process with a significant reduction in lead time." },
      { title: "Operations excellence in the backend", desc: "Setting up incident, problem and change processes for a critical platform." },
    ],
  },
  "ki-beratung": {
    slug: "ki-beratung",
    icon: "Sparkles",
    eyebrow: "Service 04",
    title: "AI Consulting & Data Strategy",
    subtitle: "Integrate artificial intelligence into existing value chains economically, securely and responsibly – from use case to production.",
    image: aiImg,
    intro:
      "AI today is no longer a technical experiment, but a strategic lever. The key is to apply it where it creates real value – with a clear business case, a sound data foundation and an operating model that also meets security, compliance and ethical requirements.",
    pillars: [
      { icon: "Lightbulb", title: "AI use-case identification", desc: "Structured identification and prioritization of AI use cases along the value chain." },
      { icon: "Database", title: "Data & MLOps strategy", desc: "Building the data foundation, platform and processes that carry productive AI solutions." },
      { icon: "ShieldCheck", title: "Responsible AI", desc: "EU AI Act, data protection, model governance and bias management – thought through cleanly from the start." },
      { icon: "Rocket", title: "Scaling & operate", desc: "From pilot to production: architecture, operations and continuous improvement of AI solutions." },
    ],
    approach: [
      { step: "01", title: "AI maturity check", desc: "Baseline assessment of data, technology, organization and use-case pipeline." },
      { step: "02", title: "Use-case mapping", desc: "Identification and assessment of relevant use cases by impact, effort and risk." },
      { step: "03", title: "Pilot & PoC", desc: "A fast, focused pilot with a clear measure of success – instead of long concept phases." },
      { step: "04", title: "Productionization", desc: "Transition into a productive, observable and governance-compliant solution." },
      { step: "05", title: "Scaling & operate", desc: "Roll-out to further use cases, continuous monitoring, model lifecycle." },
    ],
    deliverables: [
      "AI strategy and roadmap documents",
      "Use-case portfolio with prioritization and business cases",
      "Data and MLOps target picture",
      "PoC and pilot concepts including success criteria",
      "Governance, risk and EU AI Act concepts",
      "Architecture blueprints for GenAI and ML solutions",
    ],
    tech: ["Generative AI · LLMs · RAG", "Classic ML · Time-series · Vision", "MLOps · Model monitoring", "Vector DBs · Knowledge graphs", "Data Mesh · Lakehouse", "EU AI Act · GDPR · ISO 42001"],
    useCases: [
      { title: "AI assistant for vehicle data", desc: "Natural-language access to complex telematics and diagnostic data for service staff." },
      { title: "Predictive quality in rollouts", desc: "Predicting critical quality issues in international rollouts based on historic test data." },
      { title: "GenAI in customer service", desc: "Co-pilot solutions for service centres with a clean knowledge base, guardrails and audit trail." },
    ],
  },
};

export const serviceDetailsByLang = { de, en };

export const serviceOrder = [
  "it-architektur",
  "projekt-testmanagement",
  "business-analyse",
  "ki-beratung",
];

export function useServiceDetails() {
  const { lang } = useLanguage();
  return serviceDetailsByLang[lang] || serviceDetailsByLang.de;
}
